import { ChangeEvent, Fragment, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { css } from '@emotion/react';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useQueryClient } from 'react-query';

import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { QueueType } from '../models/enums/QueueType';
import { UploadType } from '../models/enums/UploadType';
import { useCreateUploadQueueItems, useSetupReprocessing } from '../api/useQueueItemController';
import { ICreateUploadQueueItemRequest } from '../models/interfaces/ICreateUploadQueueItemRequest';
import { ICreateUploadQueueItemParamRequest } from '../models/interfaces/ICreateUploadQueueItemParamRequest';
import { IQueueGroupItem, IQueueItemParam, SetupReprocessingItemParams } from '../models/interfaces/IQueueGroup';
import {
    BulkUploadColumnDef,
    findBestCsvColumnForParam,
    generateTemplateCsv,
    getColumnDefinitions,
    getExtraSubmitParams,
    getValidExtensions,
    resolveDisplayValue,
    resolveOptionValue,
} from '../utils/bulkUploadTemplate';

type ParamMappingSource =
    | { type: 'none' }
    | { type: 'csv'; csvColumn: string }
    | { type: 'fixed'; value: string };

type ParamMapping = {
    paramKey: string;
    source: ParamMappingSource;
};

type ParsedCsvData = {
    csvHeaders: string[];
    rows: Record<string, string>[];
    totalRows: number;
    paramMappings: ParamMapping[];
};

interface IQueueProcessingBulkUploadPopupProps {
    open: boolean;
    onClose: () => void;
    groupToken: string;
    queueType: QueueType;
    existingItems?: IQueueGroupItem[];
}

interface IFixedValueInputProps {
    columnDef: BulkUploadColumnDef;
    value: string;
    onChange: (newValue: string) => void;
}

const FIXED_VALUE_SENTINEL = '__fixed__';

const popupStyle = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .bulk-top-row': {
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
    },
    '& .bulk-section': {
        padding: '16px 20px',
        flex: 1,
    },
    '& .bulk-loading': {
        display: 'flex',
        justifyContent: 'center',
        padding: 32,
    },
    '& .bulk-summary': {
        display: 'flex',
        gap: 12,
        marginBottom: 16,
    },
    '& .bulk-summary-card': {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 1,
        padding: '12px 16px',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
    },
    '& .bulk-table-wrapper': {
        overflowX: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
    },
    '& .bulk-table': {
        width: '100%',
        borderCollapse: 'collapse',
    },
    '& .bulk-table th': {
        padding: '8px 12px',
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #e0e0e0',
        verticalAlign: 'top',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    '& .bulk-table td': {
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
        whiteSpace: 'nowrap',
        fontSize: 13,
    },
    '& .bulk-table tr:last-child td': {
        borderBottom: 'none',
    },
});

function getSelectValue(source: ParamMappingSource): string {
    if (source.type === 'none') {
        return '';
    }
    if (source.type === 'fixed') {
        return FIXED_VALUE_SENTINEL;
    }
    return source.csvColumn;
}

function getDefaultFixedValue(columnDef: BulkUploadColumnDef): string {
    if (columnDef.options && columnDef.options.length > 0) {
        return columnDef.options[0].value;
    }
    if (columnDef.isBool) {
        return 'true';
    }
    return '';
}

function computeValidFilesCount(
    parsedDataValue: ParsedCsvData,
    pathParamMapping: ParamMapping | undefined,
    extensions: string[]
): number {
    const dataRowCount = parsedDataValue.totalRows - 1;
    if (extensions.length === 0) {
        return dataRowCount;
    }
    if (!pathParamMapping) {
        return 0;
    }
    const source = pathParamMapping.source;
    if (source.type === 'csv') {
        return parsedDataValue.rows.filter(row => {
            const filePath = (row[source.csvColumn] ?? '').toLowerCase();
            return extensions.some(extension => filePath.endsWith(extension));
        }).length;
    }
    if (source.type === 'fixed') {
        const filePath = source.value.toLowerCase();
        return extensions.some(extension => filePath.endsWith(extension))
            ? dataRowCount
            : 0;
    }
    return 0;
}

function FixedValueInput({ columnDef, value, onChange }: IFixedValueInputProps) {
    if (columnDef.options) {
        return (
            <Select
                size='small'
                value={value}
                onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
                MenuProps={{ style: { zIndex: 1000000 } }}
                sx={{ minWidth: 140, fontSize: 12, height: 28, mt: 0.5, display: 'block' }}
            >
                {columnDef.options.map(option => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        );
    }
    if (columnDef.isBool) {
        return (
            <Select
                size='small'
                value={value}
                onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
                MenuProps={{ style: { zIndex: 1000000 } }}
                sx={{ minWidth: 140, fontSize: 12, height: 28, mt: 0.5, display: 'block' }}
            >
                <MenuItem value='true' sx={{ fontSize: 12 }}>True</MenuItem>
                <MenuItem value='false' sx={{ fontSize: 12 }}>False</MenuItem>
            </Select>
        );
    }
    return (
        <TextField
            size='small'
            value={value}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
            sx={{ minWidth: 140, mt: 0.5, display: 'block', '& .MuiInputBase-root': { height: 28, fontSize: 12 } }}
        />
    );
}

function getRowPath(row: Record<string, string>, pathParamMapping: ParamMapping | undefined): string {
    if (pathParamMapping === undefined) {
        return '';
    }
    if (pathParamMapping.source.type === 'csv') {
        return row[pathParamMapping.source.csvColumn] ?? '';
    }
    if (pathParamMapping.source.type === 'fixed') {
        return pathParamMapping.source.value;
    }
    return '';
}

function buildRowParams(
    row: Record<string, string>,
    nonPathMappings: ParamMapping[],
    columnDefs: BulkUploadColumnDef[],
    extraParams: Record<string, string>
): IQueueItemParam[] {
    const params: IQueueItemParam[] = [];
    for (const paramMapping of nonPathMappings) {
        const source = paramMapping.source;
        if (source.type === 'none') {
            continue;
        }
        const columnDef = columnDefs.find(def => def.paramKey === paramMapping.paramKey);
        let value: string;
        if (source.type === 'fixed') {
            value = source.value;
        } else if (source.type === 'csv') {
            const rawValue = row[source.csvColumn] ?? '';
            const resolvedValue = columnDef ? resolveOptionValue(rawValue, columnDef) : null;
            value = resolvedValue ?? rawValue;
        } else {
            continue;
        }
        params.push({ Param: paramMapping.paramKey, Value: value });
    }
    for (const [extraKey, extraValue] of Object.entries(extraParams)) {
        params.push({ Param: extraKey, Value: extraValue });
    }
    return params;
}

export function QueueProcessingBulkUploadPopup({ open, onClose, groupToken, queueType, existingItems }: IQueueProcessingBulkUploadPopupProps) {
    const [parsedData, setParsedData] = useState<ParsedCsvData | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [updateOnly, setUpdateOnly] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const columnDefs = getColumnDefinitions(queueType);
    const queryClient = useQueryClient();
    const { mutateAsync: createItems, isLoading: isCreating } = useCreateUploadQueueItems();
    const { mutateAsync: setupReprocessing, isLoading: isUpdating } = useSetupReprocessing();
    const isSubmitting = isCreating || isUpdating;

    const handleDownloadTemplate = () => {
        const csvContent = generateTemplateCsv(queueType);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'bulk_upload_template.csv';
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const parseCsv = (text: string): ParsedCsvData => {
        const allLines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
        if (allLines.length === 0) {
            return { csvHeaders: [], rows: [], totalRows: 0, paramMappings: [] };
        }
        const [headerLine, ...dataLines] = allLines;
        const csvHeaders = headerLine.split(',').map(header => header.trim());
        const rows: Record<string, string>[] = dataLines.map(line => {
            const values = line.split(',');
            const row: Record<string, string> = {};
            csvHeaders.forEach((header, index) => {
                row[header] = (values[index] ?? '').trim();
            });
            return row;
        });
        const paramMappings: ParamMapping[] = columnDefs.map(columnDef => {
            const csvColumn = findBestCsvColumnForParam(columnDef.paramKey, csvHeaders, columnDefs);
            const source: ParamMappingSource = csvColumn !== null
                ? { type: 'csv', csvColumn }
                : { type: 'none' };
            return { paramKey: columnDef.paramKey, source };
        });
        return { csvHeaders, rows, totalRows: allLines.length, paramMappings };
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setIsParsing(true);
        const reader = new FileReader();
        reader.onload = (readEvent) => {
            const text = readEvent.target?.result as string;
            const data = parseCsv(text);
            setParsedData(data);
            setIsParsing(false);
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    const handleMappingChange = (paramKey: string, newValue: string) => {
        if (!parsedData) {
            return;
        }
        const columnDef = columnDefs.find(def => def.paramKey === paramKey);
        let newSource: ParamMappingSource;
        if (newValue === '') {
            newSource = { type: 'none' };
        } else if (newValue === FIXED_VALUE_SENTINEL) {
            newSource = { type: 'fixed', value: columnDef ? getDefaultFixedValue(columnDef) : '' };
        } else {
            newSource = { type: 'csv', csvColumn: newValue };
        }
        setParsedData({
            ...parsedData,
            paramMappings: parsedData.paramMappings.map(mapping =>
                mapping.paramKey === paramKey
                    ? { ...mapping, source: newSource }
                    : mapping
            ),
        });
    };

    const handleFixedValueChange = (paramKey: string, newValue: string) => {
        if (!parsedData) {
            return;
        }
        setParsedData({
            ...parsedData,
            paramMappings: parsedData.paramMappings.map(mapping =>
                mapping.paramKey === paramKey
                    ? { ...mapping, source: { type: 'fixed', value: newValue } }
                    : mapping
            ),
        });
    };

    const handleSubmitCreate = async () => {
        if (!parsedData) {
            return;
        }
        const pathParamMapping = parsedData.paramMappings.find(mapping => mapping.paramKey === 'Path');
        const nonPathMappings = parsedData.paramMappings.filter(mapping => mapping.paramKey !== 'Path');
        const extraParams = getExtraSubmitParams(queueType);
        const items: ICreateUploadQueueItemRequest[] = parsedData.rows.map(row => ({
            Priority: 0,
            SizeBytes: null,
            Path: getRowPath(row, pathParamMapping),
            Paths: [],
            Params: buildRowParams(row, nonPathMappings, columnDefs, extraParams) as ICreateUploadQueueItemParamRequest[],
        }));
        await createItems({
            GroupToken: groupToken,
            UploadType: UploadType.ServerFileUpload,
            Items: items,
        });
        queryClient.invalidateQueries(['QueueGroupById', groupToken]);
        handleClose();
    };

    const handleSubmitUpdate = async () => {
        if (!parsedData || !existingItems) {
            return;
        }
        const pathParamMapping = parsedData.paramMappings.find(mapping => mapping.paramKey === 'Path');
        const nonPathMappings = parsedData.paramMappings.filter(mapping => mapping.paramKey !== 'Path');
        const extraParams = getExtraSubmitParams(queueType);
        const updateItems: SetupReprocessingItemParams[] = [];
        for (const row of parsedData.rows) {
            const rowPath = getRowPath(row, pathParamMapping).toLowerCase();
            if (!rowPath) {
                continue;
            }
            const matchingItem = existingItems.find(item =>
                (item.Params.find(param => param.Param === 'FilePath')?.Value ?? '').toLowerCase() === rowPath
            );
            if (!matchingItem) {
                continue;
            }
            updateItems.push({
                Token: matchingItem.Token,
                Params: buildRowParams(row, nonPathMappings, columnDefs, extraParams),
            });
        }
        if (updateItems.length === 0) {
            return;
        }
        await setupReprocessing({ Items: updateItems });
        queryClient.invalidateQueries(['QueueGroupById', groupToken]);
        handleClose();
    };

    const handleSubmit = async () => {
        if (updateOnly) {
            await handleSubmitUpdate();
        } else {
            await handleSubmitCreate();
        }
    };

    const handleClose = () => {
        setParsedData(null);
        onClose();
    };

    const csvHeaders = parsedData?.csvHeaders ?? [];
    const paramMappings = parsedData?.paramMappings ?? [];
    const rows = parsedData?.rows ?? [];

    const csvColumnOptions: { value: string; label: string }[] = parsedData
        ? [
            { value: '', label: 'Not mapped' },
            ...csvHeaders.map(header => ({ value: header, label: header })),
        ]
        : [];

    const pathMapping = paramMappings.find(mapping => mapping.paramKey === 'Path');
    const validExtensions = getValidExtensions(queueType);
    const validFilesCount = parsedData === null
        ? 0
        : computeValidFilesCount(parsedData, pathMapping, validExtensions);

    const dataRowCount = parsedData !== null ? parsedData.totalRows - 1 : 0;
    const isPathMapped = pathMapping !== undefined && pathMapping.source.type !== 'none';
    const canSubmit = parsedData !== null && dataRowCount > 0 && isPathMapped && !isSubmitting;
    const previewCount = Math.min(10, dataRowCount);
    const validFilesColor = parsedData !== null && validFilesCount === dataRowCount
        ? '#388e3c'
        : '#e65100';

    const footerElement = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
                variant='outlined'
                size='small'
                onClick={handleClose}
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
            >
                Cancel
            </Button>
            <Button
                variant='contained'
                size='small'
                disableElevation
                onClick={handleSubmit}
                disabled={!canSubmit}
                startIcon={isSubmitting ? <CircularProgress size={14} color='inherit' /> : undefined}
                sx={{ textTransform: 'none' }}
            >
                {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
        </Box>
    );

    return (
        <GeopostPopup
            open={open}
            onChangeOpen={(value) => {
                if (!value) {
                    handleClose();
                }
            }}
            title='Bulk Upload'
            width={1350}
            height={864}
            draggable
            overlay
            resizable
            footer={footerElement}
        >
            <Box css={popupStyle}>
                <Box className='bulk-top-row'>
                    <Box className='bulk-section'>
                        <Typography variant='body2' sx={{ fontWeight: 600, color: '#424242', mb: 0.5 }}>
                            Download Template
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                            Download the CSV template with the required columns.
                        </Typography>
                        <Button
                            variant='outlined'
                            size='small'
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadTemplate}
                            sx={{ textTransform: 'none' }}
                        >
                            Download CSV Template
                        </Button>
                    </Box>

                    <Divider orientation='vertical' flexItem />

                    <Box className='bulk-section'>
                        <Typography variant='body2' sx={{ fontWeight: 600, color: '#424242', mb: 0.5 }}>
                            Upload CSV
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                            Select a CSV file to import files into the queue.
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='.csv'
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <Button
                            variant='contained'
                            size='small'
                            disableElevation
                            startIcon={<UploadFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ textTransform: 'none' }}
                        >
                            Select CSV File
                        </Button>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size='small'
                                    checked={updateOnly}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setUpdateOnly(event.target.checked)}
                                />
                            }
                            label={<Typography variant='body2'>Apenas atualizar itens existentes</Typography>}
                            sx={{ mt: 1, display: 'block' }}
                        />
                    </Box>
                </Box>

                {isParsing && (
                    <Fragment>
                        <Divider />
                        <Box className='bulk-loading'>
                            <CircularProgress />
                        </Box>
                    </Fragment>
                )}

                {parsedData !== null && !isParsing && (
                    <Fragment>
                        <Divider />
                        <Box className='bulk-section'>
                            <Box className='bulk-summary'>
                                <Box className='bulk-summary-card'>
                                    <Typography variant='h6' sx={{ fontWeight: 700, color: '#1976d2', lineHeight: 1 }}>
                                        {dataRowCount}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Files to process
                                    </Typography>
                                </Box>
                                <Box className='bulk-summary-card'>
                                    <Typography variant='h6' sx={{ fontWeight: 700, color: validFilesColor, lineHeight: 1 }}>
                                        {validFilesCount}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Valid file extensions
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant='body2' sx={{ fontWeight: 600, color: '#424242', mb: 1 }}>
                                {`Preview — first ${previewCount} ${previewCount === 1 ? 'row' : 'rows'}`}
                            </Typography>
                            <Box className='bulk-table-wrapper'>
                                <table className='bulk-table'>
                                    <thead>
                                        <tr>
                                            {columnDefs.map(columnDef => {
                                                const mapping = paramMappings.find(paramMapping => paramMapping.paramKey === columnDef.paramKey);
                                                const source: ParamMappingSource = mapping?.source ?? { type: 'none' };
                                                const selectValue = getSelectValue(source);
                                                return (
                                                    <th key={columnDef.paramKey}>
                                                        <Typography variant='caption' sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                                            {columnDef.label}
                                                        </Typography>
                                                        <Select
                                                            size='small'
                                                            value={selectValue}
                                                            onChange={(selectEvent: SelectChangeEvent<string>) =>
                                                                handleMappingChange(columnDef.paramKey, selectEvent.target.value)
                                                            }
                                                            MenuProps={{ style: { zIndex: 1000000 } }}
                                                            sx={{ minWidth: 140, fontSize: 12, height: 28 }}
                                                        >
                                                            {csvColumnOptions.map(option => (
                                                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                            <MenuItem value={FIXED_VALUE_SENTINEL} sx={{ fontSize: 12 }}>
                                                                Set Value
                                                            </MenuItem>
                                                        </Select>
                                                        {source.type === 'fixed' && (
                                                            <FixedValueInput
                                                                columnDef={columnDef}
                                                                value={source.value}
                                                                onChange={(newValue) => handleFixedValueChange(columnDef.paramKey, newValue)}
                                                            />
                                                        )}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, 10).map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {columnDefs.map(columnDef => {
                                                    const mapping = paramMappings.find(paramMapping => paramMapping.paramKey === columnDef.paramKey);
                                                    let cellContent = '';
                                                    if (mapping) {
                                                        if (mapping.source.type === 'csv') {
                                                            cellContent = resolveDisplayValue(row[mapping.source.csvColumn] ?? '', columnDef);
                                                        } else if (mapping.source.type === 'fixed') {
                                                            const fixedValue = mapping.source.value;
                                                            if (columnDef.options) {
                                                                const option = columnDef.options.find(opt => opt.value === fixedValue);
                                                                cellContent = option ? option.label : fixedValue;
                                                            } else {
                                                                cellContent = fixedValue;
                                                            }
                                                        }
                                                    }
                                                    return (
                                                        <td key={columnDef.paramKey}>
                                                            <Typography variant='caption'>{cellContent}</Typography>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Box>
                    </Fragment>
                )}
            </Box>
        </GeopostPopup>
    );
}
